import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RejectionReason } from '../utils/rejectionReasons';
import { XIcon as X, AlertCircleIcon as AlertCircle, ImageIcon, UploadIcon as Upload, Trash2Icon as Trash2 } from '../../icons';
import { sendToTerminal } from '../../../lib/terminalLogger';
import { Modal, Button } from '../../ui';

interface RefuseModalProps {
  onClose: () => void;
  onSubmit?: (data: {
    motivo: string;
    detalhes: string;
    observacao: string;
    imagens: string[];
  }) => Promise<void> | void;
  pedidoId: number | string;
  itemId: number | string;
  motivosPrincipais?: RejectionReason[] | string[];
  detalhesSugeridos?: string[];
}

// Valores default caso não sejam passados por props
const DEFAULT_MOTIVOS = [
  'Baixa resolução',
  'Fonte incorreta',
  'Cores divergentes (ICC)',
  'Tamanho / escala incorretos',
  'Sangria / margens inadequadas',
  'Elementos cortados / vazando',
  'Outros'
];
// const DEFAULT_DETALHES: string[] = [];

const MAX_IMAGENS = 5;
const MAX_OBSERVACAO_CHARS = 600;

export function RefuseModal({ onClose, onSubmit, motivosPrincipais }: RefuseModalProps) {
  const [motivo, setMotivo] = useState<string>('');
  const [motivoDescricao, setMotivoDescricao] = useState<string>('');
  // const [detalhes, setDetalhes] = useState<string[]>([]);
  const [observacao, setObservacao] = useState<string>('');
  const [imagens, setImagens] = useState<File[]>([]);
  const [errors, setErrors] = useState<{ motivo?: string; imagens?: string } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dropRef = useRef<HTMLDivElement | null>(null);

  const canSubmit = useMemo(() => {
    return motivo.trim().length > 0 && imagens.length <= MAX_IMAGENS;
  }, [motivo, imagens.length]);

  // Detalhes não usados no fluxo atual (mantido para futura expansão)
  // const handleToggleDetalhe = (_d: string) => {};
  // const clearDetalhes = () => setDetalhes([]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const acceptable = Array.from(files).filter(f => /image\/(png|jpe?g)/i.test(f.type));
    const next = [...imagens, ...acceptable].slice(0, MAX_IMAGENS);
    if (imagens.length + acceptable.length > MAX_IMAGENS) {
      setErrors({ ...(errors || {}), imagens: `Máximo de ${MAX_IMAGENS} imagens.` });
    } else {
      setErrors(prev => prev && { ...prev, imagens: undefined });
    }
    setImagens(next);
  }, [imagens, errors]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
  };

  const handlePaste = useCallback((e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === 'file') {
        const file = it.getAsFile();
        if (file && /image\/(png|jpe?g)/i.test(file.type)) files.push(file);
      }
    }
    if (files.length) addFiles(files);
  }, [addFiles]);

  useEffect(() => {
    const node = dropRef.current;
    if (!node) return;
    const handler = (ev: ClipboardEvent) => handlePaste(ev);
    node.addEventListener('paste', handler as any);
    return () => node.removeEventListener('paste', handler as any);
  }, [handlePaste]);

  const removeImage = (idx: number) => setImagens(prev => prev.filter((_, i) => i !== idx));

  const validate = () => {
    const next: { motivo?: string; imagens?: string } = {};
    if (!motivo.trim()) next.motivo = 'Selecione um motivo';
    if (imagens.length > MAX_IMAGENS) next.imagens = `Máximo de ${MAX_IMAGENS} imagens.`;
    setErrors(Object.keys(next).length ? next : null);
    return Object.keys(next).length === 0;
  };

  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          resolve(result);
        } else {
          reject(new Error('Falha ao ler imagem.'));
        }
      };
      reader.onerror = () => reject(new Error('Erro ao processar imagem.'));
      reader.readAsDataURL(file);
    });
  }, []);

  const submit = async () => {
    if (!validate() || isSubmitting) return;
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const imagensBase64 = await Promise.all(imagens.map(async (file, idx) => {
        try { sendToTerminal('🖼️ [RECUSA] Convertendo imagem', { index: idx + 1, total: imagens.length, nome: file.name }); } catch {}
        return await fileToBase64(file);
      }));
      try { sendToTerminal('🚀 [RECUSA] Imagens convertidas, enviando payload', { imagensCount: imagensBase64.length }); } catch {}

      const detalhes = motivoDescricao ? motivoDescricao.trim() : '';
      if (onSubmit) {
        await onSubmit({
          motivo: motivo.trim(),
          detalhes,
          observacao: observacao.trim(),
          imagens: imagensBase64,
        });
      }
      setIsSubmitting(false);
      setMotivo('');
      setObservacao('');
      setImagens([]);
      onClose();
    } catch (error: any) {
      setIsSubmitting(false);
      setSubmitError(error?.message || 'Erro ao enviar recusa.');
      try { sendToTerminal('❌ [RECUSA] Erro durante conversão/envio', { error: error?.message || String(error) }); } catch {}
    }
  };

  // Atualizar título/descrição conforme seleção
  useEffect(() => {
    const list = (motivosPrincipais && motivosPrincipais.length ? motivosPrincipais : DEFAULT_MOTIVOS) as any[];
    if (!motivo) { setMotivoDescricao(''); return; }
    const found = list.find((m) => typeof m === 'string' ? m === motivo : (m as RejectionReason).id === motivo || (m as RejectionReason).title === motivo);
    if (!found) { setMotivoDescricao(''); return; }
    if (typeof found === 'string') {
      setMotivoDescricao('');
    } else {
      setMotivoDescricao((found as RejectionReason).description || '');
    }
  }, [motivo, motivosPrincipais]);

  return (
    <Modal 
      isOpen={true}
      title="Recusar arte"
      onClose={onClose}
      size="lg"
      className="max-w-full min-w-[255px] sm:max-w-lg"
    >
      <div className="space-y-3 overflow-y-auto max-h-[60vh]">
        <div className="grid grid-cols-1 gap-3">
          <label className="block">
            <span className="text-sm text-gray-200 font-medium">Motivo principal</span>
            <select
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              className="mt-1 w-full bg-navy-700 text-white p-2 rounded-md border border-navy-600 focus:outline-none focus:ring-1 focus:ring-yellow-400"
              aria-label="Motivo principal"
            >
              <option value="">Selecione…</option>
              {(() => {
                const list = (motivosPrincipais && motivosPrincipais.length ? motivosPrincipais : DEFAULT_MOTIVOS);
                return (list as any[]).map((m) => {
                  if (typeof m === 'string') return <option key={m} value={m}>{m}</option>;
                  const rm = m as RejectionReason;
                  return <option key={rm.id} value={rm.id}>{rm.title}</option>;
                });
              })()}
            </select>
            {errors?.motivo && <div className="text-xs text-red-400 mt-1">{errors.motivo}</div>}
          </label>

          {/* Texto explicativo do motivo selecionado */}
          <div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-200 font-medium">Detalhes do motivo</span>
            </div>
            <div
              className={`mt-1 rounded-md border ${motivo ? 'border-navy-600 bg-navy-700/60 text-gray-100' : 'border-navy-700 bg-navy-800/60 text-gray-500'} p-2 text-[12px] leading-relaxed min-h-[48px]`}
              aria-live="polite"
            >
              {motivo ? (motivoDescricao || '—') : 'Selecione um motivo principal para visualizar a orientação.'}
            </div>
          </div>

          <label className="block">
            <span className="text-sm text-gray-200 font-medium">Observação (opcional)</span>
            <textarea
              value={observacao}
              onChange={e => setObservacao(e.target.value.slice(0, MAX_OBSERVACAO_CHARS))}
              className="mt-1 w-full bg-navy-700 text-white p-2 rounded-md border border-navy-600 min-h-[96px] placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-yellow-400"
              placeholder="Descreva o problema encontrado na arte…"
            />
            <div className="text-[11px] text-gray-500 mt-1">{observacao.length}/{MAX_OBSERVACAO_CHARS}</div>
          </label>

          <div>
            <span className="text-sm text-gray-200 font-medium">Anexos (até {MAX_IMAGENS} imagens)</span>
            <div
              ref={dropRef}
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              className="mt-1 rounded-md border border-dashed border-navy-600 bg-navy-700/40 p-3 text-center text-gray-300"
              title="Arraste, clique ou cole imagens aqui"
            >
              <div className="flex flex-col items-center gap-1">
                <ImageIcon className="w-5 h-5 text-gray-400" />
                <span className="text-xs">Arraste ou cole imagens aqui</span>
                <label className="mt-1 inline-flex items-center gap-1 px-2 py-1 rounded bg-navy-700 hover:bg-navy-600 cursor-pointer text-xs">
                  <Upload className="w-3.5 h-3.5" />
                  <span>Selecionar</span>
                  <input type="file" accept="image/png,image/jpeg" multiple className="hidden" onChange={handleFileInput} />
                </label>
              </div>
            </div>
            {errors?.imagens && <div className="text-xs text-red-400 mt-1">{errors.imagens}</div>}

            {imagens.length > 0 && (
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto pr-1">
                {imagens.map((file, idx) => {
                  const url = URL.createObjectURL(file);
                  return (
                    <div key={idx} className="relative group">
                      <img src={url} alt={`anexo-${idx+1}`} className="w-full h-16 object-cover rounded border border-navy-700" />
                      <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 p-1 rounded bg-navy-900/80 text-gray-200 hover:text-white">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {errors && !canSubmit && (
          <div className="flex items-center gap-2 text-red-400 text-xs">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>Verifique os campos obrigatórios.</span>
          </div>
        )}

      </div>

      <div className="mt-4 pt-4 border-t border-navy-700 bg-navy-800 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
        {submitError && (
          <div className="flex-1 text-xs text-red-300 bg-red-900/30 border border-red-500/40 rounded px-2 py-1">
            {submitError}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <Button 
            variant="secondary" 
            onClick={isSubmitting ? undefined : onClose} 
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            disabled={!canSubmit || isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Enviando…' : 'Enviar recusa'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default RefuseModal;


